import { Theme } from "@material-ui/core";
import { Overrides } from "@material-ui/core/styles/overrides";
import { MuiPickersOverrides } from "@material-ui/pickers/typings/overrides";

import { baseColors, spacing } from "./baseColors";
import { palette } from "./palette";

const inputUnderline: any = {
    "&:before": {
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
        borderBottomColor: baseColors.inactive_gray,
    },
    "&:hover:not($disabled):not($error):not($focused):before": {
        borderBottomStyle: "solid",
        borderBottomWidth: 2,
        borderBottomColor: baseColors.inactive_gray,
    },
    "&:hover:not($disabled):not($error):after": {
        borderBottomStyle: "solid",
        borderBottomWidth: 2,
        borderBottomColor: palette.secondary.dark,
    },
    "&:after": {
        borderBottomStyle: "solid",
        borderBottomWidth: 2,
        borderBottomColor: palette.secondary.dark,
    },
};

type ComboOverrides = Overrides & MuiPickersOverrides;

export const getControlOverrides = (theme: Theme): ComboOverrides => ({
    MuiButton: {
        contained: {
            "&:hover": {
                backgroundColor: baseColors.white,
                "@media (hover: none)": {
                    backgroundColor: baseColors.white,
                },
            },
            "&$disabled": {
                backgroundColor: baseColors.inactive_gray,
                color: baseColors.white,
            },
            backgroundColor: baseColors.white,
            borderRadius: "4px",
            boxShadow:
                "0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 1px 0px rgba(0,0,0,0.1), 0px 1px 1px -1px rgba(0,0,0,0.1)",
            color: baseColors.header_purple,
        },
        containedPrimary: {
            "&:hover": {
                backgroundColor: baseColors.hover_green,
                "@media (hover: none)": {
                    backgroundColor: baseColors.hover_green,
                },
            },
            backgroundColor: baseColors.green,
            boxShadow: "0 1px 1px 0 rgba(0,0,0,0.10)",
            color: baseColors.header_purple,
        },
        containedSecondary: {
            "&:hover": {
                backgroundColor: baseColors.hover_danger_red,
                "@media (hover: none)": {
                    backgroundColor: baseColors.hover_danger_red,
                },
            },
            backgroundColor: baseColors.danger_red,
            boxShadow: "0 1px 1px 0 rgba(0,0,0,0.10)",
            color: baseColors.white,
        },
        iconSizeLarge: {
            fontSize: "40px",
            "& > *:first-child": {
                fontSize: "40px",
            },
        },
        iconSizeMedium: {
            fontSize: "24px",
            "& > *:first-child": {
                fontSize: "24px",
            },
        },
        iconSizeSmall: {
            fontSize: "16px",
            "& > *:first-child": {
                fontSize: "16px",
            },
        },

        label: {
            textTransform: "lowercase",
        },
        root: {
            "&:active": {
                backgroundColor: "none",
            },
            "&:hover": {
                backgroundColor: "none",
            },
            // eslint-disable-next-line quotes
            fontFamily: ['"hkgrotesk_bold"', "Helvetica", "sans-serif"].join(
                ","
            ),
            fontSize: "16px",
            fontWeight: "bold",
            fontStyle: "normal",
            letterSpacing: 0.5,
            lineHeight: "32px",
            padding: 0,
            paddingLeft: 32,
            paddingRight: 32,
        },
        sizeLarge: {
            "&:active": {
                boxShadow: "0 1px 1px 0 rgba(0,0,0,0.04), 0 0",
                transform: "scale(.99)",
            },
            "&:hover": {
                boxShadow: "0 2px 12px 0 rgba(0,0,0,0.10)",
                transform: "translateY(-1px)",
            },
            fontSize: "16px",
            height: "56px",
            padding: "6px 32px",
        },
        sizeSmall: {
            "&:active": {
                boxShadow:
                    "0 1px 1px 0 rgba(0,0,0,0.10), 0 1px 10px 0 rgba(0,0,0,0.10)",
                transform: "scale(.99)",
            },
            "&:hover": {
                boxShadow: "0 2px 24px 0 rgba(0,0,0,0.10)",
                transform: "translateY(-1px)",
            },
            fontSize: "16px",
            height: "40px",
            padding: "4px 20px",
        },
        text: {
            "&:active": {
                boxShadow: "none",
            },
            "&:hover": {
                boxShadow: "none",
            },
            borderRadius: 0,
            color: baseColors.header_purple,
            padding: 0,
        },
        textPrimary: {
            "&:hover": {
                backgroundColor: "none",
                boxShadow: `0 2px 0px 0px ${baseColors.green}, 0 -0.8em 0 0 ${baseColors.green} inset`,
            },
            boxShadow: `0 2px 0px 0px ${baseColors.green}, 0 0 0 0 ${baseColors.green} inset`,
            transition: "box-shadow 400ms cubic-bezier(0.33, 0, 0, 1)",
        },
        textSecondary: {
            "&:hover": {
                backgroundColor: "none",
                boxShadow: `0 2px 0px 0px ${baseColors.magenta}, 0 -0.8em 0 0 ${baseColors.magenta} inset`,
            },
            boxShadow: `0 2px 0px 0px ${baseColors.magenta}, 0 0 0 0 ${baseColors.magenta} inset`,
            transition: "box-shadow 400ms cubic-bezier(0.33, 0, 0, 1)",
        },
    },
    MuiButtonBase: {
        root: {
            // eslint-disable-next-line quotes
            fontFamily: ['"hkgrotesk_regular"', "Helvetica", "sans-serif"].join(
                ","
            ),
            fontWeight: "normal",
            fontStyle: "normal",
        },
    },
    MuiFab: {
        primary: {
            "&:hover": {
                backgroundColor: baseColors.hover_green,
            },
            backgroundColor: baseColors.green,
            color: baseColors.header_purple,
        },
        root: {
            "&:hover": {
                backgroundColor: baseColors.white,
            },
            "&$disabled": {
                backgroundColor: baseColors.inactive_gray,
                color: baseColors.white,
            },
            backgroundColor: baseColors.white,
            boxShadow: "0 1px 1px 0 rgba(0,0,0,0.10)",
            color: baseColors.header_purple,
            // eslint-disable-next-line quotes
            fontFamily: ['"hkgrotesk_bold"', "Helvetica", "sans-serif"].join(
                ","
            ),
            fontWeight: "bold",
            fontStyle: "normal",
            fontSize: "16px",
            letterSpacing: 0.5,
            lineHeight: "32px",
        },
        secondary: {
            "&:hover": {
                backgroundColor: baseColors.hover_danger_red,
            },
            backgroundColor: baseColors.danger_red,
            color: baseColors.white,
        },
        sizeSmall: {
            "&:active": {
                boxShadow:
                    "0 1px 1px 0 rgba(0,0,0,0.10), 0 1px 10px 0 rgba(0,0,0,0.10)",
                transform: "scale(.99)",
            },
            "&:hover": {
                boxShadow: "0 2px 24px 0 rgba(0,0,0,0.10)",
                transform: "translateY(-1px)",
            },
        },
    },
    MuiFilledInput: {
        underline: inputUnderline,
    },
    MuiFormControl: {
        root: {
            margin: `${spacing * 6}px ${spacing * 3}px ${spacing * 3}px ${spacing * 3
                }px`,
        },
    },
    MuiFormControlLabel: {
        label: {
            fontSize: "16px",
        },
    },
    MuiIconButton: {
        root: {
            "&$disabled": {
                backgroundColor: "none",
            },
        },
    },
    MuiInputBase: {
        root: {
            fontSize: "16px",
            lineHeight: "24px",
            //display: "unset",
        },
        input: {
            height: "unset",
        },
    },
    MuiInput: {
        underline: inputUnderline,
    },
    MuiInputAdornment: {
        root: {
            fontSize: "16px",
            lineHeight: "24px",
        },
    },
    MuiInputLabel: {
        root: {
            color: "rgba(0,0,0,0.54)",
            fontSize: "16px",
        },
        shrink: {
            transform: "translate(0, 1.5px) scale(0.875)",
        },
    },
    MuiListItem: {
        button: {
            "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.07)",
            },
        },
    },
    MuiMenuItem: {
        root: {
            fontSize: "16px",
        },
    },
    MuiPickersToolbar: {
        toolbar: {
            backgroundColor: palette.secondary.main,
        },
    },
    MuiSelect: {
        icon: {
            color: baseColors.inactive_gray,
        },
    },
    MuiStepIcon: {
        active: {
            color: palette.primary.light,
        },
        completed: {
            color: palette.secondary.main,
        },
        root: {
            "&$active": {
                color: palette.primary.main,
            },
            "&$completed": {
                color: palette.secondary.main,
            },
        },
    },
    MuiSvgIcon: {
        fontSizeLarge: {
            fontSize: "40px",
        },
        fontSizeSmall: {
            fontSize: "16px",
        },
        root: {
            fontSize: "24px",
        },
    },
    MuiTableCell: {
        body: {
            color: baseColors.black,
            fontSize: "16px",
        },
    },
    MuiTableRow: {
        root: {
            "&$hover:hover": {
                backgroundColor: "rgba(0,0,0,0.07)",
            },
        },
    },
    MuiTooltip: {
        tooltip: {
            fontSize: "14px",
            padding: spacing,
        },
    },
    MuiTypography: {
        h1: {
            [theme.breakpoints.down("xs")]: {
                fontSize: "24px",
            },
        },
        h2: {
            [theme.breakpoints.down("xs")]: {
                fontSize: "20px",
            },
        },
        h3: {
            [theme.breakpoints.down("xs")]: {
                fontSize: "20px",
            },
        },
        h4: {
            [theme.breakpoints.down("xs")]: {
                fontSize: "18px",
            },
        },
        h5: {
            [theme.breakpoints.down("xs")]: {
                fontSize: "16px",
            },
        },
        h6: {
            [theme.breakpoints.down("xs")]: {
                fontSize: "16px",
            },
        },
    },
    MuiSnackbar: {
        anchorOriginBottomRight: {
            "@media (max-width: 1023.98px)": {
                marginBottom: spacing * 8, // = mobile footer actions bar height
            },
        },
    },
});
